/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import { TextInput } from 'views/components/TextInput/TextInput';
import { injectIntl, IntlShape } from 'react-intl';
import { SelectInput } from 'views/components/SelectInput/SelectInput';
import { Button } from 'views/components/Button/Button';
import { PayCycles, IPayRun } from 'types';
import { Icon } from 'views/components/Icon/Icon';

export const MAX_EMPLOYEE_COUNT = 300;

interface Props {
  intl: IntlShape;
  initialValue: IPayRun[],
  onChange: (payRuns: IPayRun[]) => void;
  touchedNext: boolean;
  noPayRunErrorMessage: string;
  employeeCountErrorMessage: string;
  payCycleErrorMessage: string;
  employeeMaxErrorMessage: string;
  payCycles: string[];
}

// If the initialValue is an empty array then pre-populate it with a IPayRuns object
function defaultInitialValue(initialValue: IPayRun[]): IPayRun[] {
  if (initialValue.length === 0) {
    return [{
      employeeCount: undefined,
      payCycle: undefined,
    }];
  }

  return initialValue;
}

// @NOTE Upper employeeCount limit of 300

export const PayRunsComponent = (props: Props) => {
  const {
    intl,
    onChange,
    initialValue,
    touchedNext,
    noPayRunErrorMessage,
    employeeCountErrorMessage,
    payCycleErrorMessage,
    employeeMaxErrorMessage,
    payCycles,
  } = props;

  const [payRuns, setPayRuns] = useState<IPayRun[]>(defaultInitialValue(initialValue));

  const selectedPayCycles = payRuns.reduce((accumulator: string[], { payCycle }) => {
    if (payCycle) {
      return [
        ...accumulator,
        payCycle,
      ];
    }

    return accumulator;
  }, []);

  const setEmployeeCyclesFor = (
    employeeCycleIndex: number,
    employeeCount?: number,
    payCycle?: PayCycles,
  ) => {
    const payCycleToUpdate = payRuns.find(
      (_, index) => index === employeeCycleIndex,
    );

    if (payCycleToUpdate) {
      const employeeCyclesToUpdate = [...payRuns];
      employeeCyclesToUpdate[employeeCycleIndex].employeeCount = employeeCount;
      employeeCyclesToUpdate[employeeCycleIndex].payCycle = payCycle;

      setPayRuns(employeeCyclesToUpdate);
    }
  };

  const addPayCycle = () => {
    // Create new entry
    const employeeCyclesToUpdate = [...payRuns];

    // Don't add more employeeCycles than payCycles available
    if (employeeCyclesToUpdate.length >= payCycles.length) {
      return;
    }

    employeeCyclesToUpdate.push({
      employeeCount: undefined,
      payCycle: undefined,
    });

    setPayRuns(employeeCyclesToUpdate);
  };

  const removePayCycle = () => {
    // Remove existing entry (starting with the last one)
    const employeeCyclesToUpdate = [...payRuns];

    // Don't remove the first 1
    if (employeeCyclesToUpdate.length > 1) {
      employeeCyclesToUpdate.pop();
      setPayRuns(employeeCyclesToUpdate);
    }
  };

  useEffect(() => {
    onChange(payRuns);
  }, [payRuns, onChange]);

  // Need at least 1 pay run
  const noPayRun = payRuns.length === 0 || (!payRuns[0].employeeCount && !payRuns[0].payCycle);

  return (
    <div className="PayRuns">
      {payRuns.map(({ employeeCount, payCycle }, index) => {
        const missingEmployeeCount = !employeeCount;
        const missingPayCycle = !payCycle;

        let employeeCountError = '';
        let payCycleError = '';

        const isPayRunEmpty = !!missingEmployeeCount !== !!missingPayCycle;

        // Will only show missing errors if there's already 1 field filled out but they've missed the other
        if (touchedNext && !noPayRun && isPayRunEmpty) {
          if (missingEmployeeCount) {
            employeeCountError = employeeCountErrorMessage;
          } else if (missingPayCycle) {
            payCycleError = payCycleErrorMessage;
          }
        }

        if (touchedNext && employeeCount && employeeCount > MAX_EMPLOYEE_COUNT) {
          employeeCountError = employeeMaxErrorMessage;
        }

        return (
          <div className="PayRuns-item" key={`employee-cycle-${index}`}>

            <TextInput
              className="PayRuns-amount"
              id={`employee-count-${index}`}
              type="number"
              label={intl.formatMessage({ id: 'EMPLOYEE_COUNT' })}
              placeholder={intl.formatMessage({ id: 'AMOUNT' })}
              onChange={(value) => {
                setEmployeeCyclesFor(
                  index,
                  parseInt(value, 10) || undefined,
                  payCycle,
                );
              }}
              value={`${employeeCount}`}
              error={employeeCountError}
              showErrorOutLine={touchedNext && noPayRun}
            />

            <SelectInput
              className="PayRuns-payCycle"
              id={`pay-cycle-${index}`}
              label={intl.formatMessage({ id: 'PAY_CYCLE' })}
              placeholder="Select"
              // placeholder={intl.formatMessage({ id: 'SELECT' })}
              onChange={(value) => {
                setEmployeeCyclesFor(index, employeeCount, value as PayCycles);
              }}
              options={payCycles}
              defaultValue={payCycle}
              disabledOptions={selectedPayCycles}
              error={touchedNext && payCycleError}
              showErrorOutLine={touchedNext && noPayRun}
            />

            <div className="PayRuns-remove">
              {/* Show remove/delete icon for all rows except the first one */}
              {index !== 0 && (
              <Button
                onClick={() => removePayCycle()}
                label={intl.formatMessage({ id: 'ADD_PAY_CYCLE' })}
                icon="ic-close"
                className="Button"
                isIconOnly
              />
              )}
            </div>
          </div>
        );
      })}

      {/* Error - no pay run at all */}
      {touchedNext && noPayRun && (
        <div className="PayRuns-error">
          <span className="TextInput-error">
            <Icon name="ic-exclamation" />{noPayRunErrorMessage}
          </span>
        </div>
      )}

      <div className="PayRuns-add">
        {/* We can only add additional pay cycles based on the pay cycle array */}
        {payRuns.length < payCycles.length && (
          <Button
            onClick={() => addPayCycle()}
            label={intl.formatMessage({ id: 'ADD_PAY_CYCLE' })}
            icon="ic-add"
            className="Button"
          />
        )}
      </div>
    </div>
  );
};

export const PayRuns = injectIntl(PayRunsComponent);
